// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

// components
import { Button } from '@/components/atoms/Button/Button';
import { Checkbox } from '@/components/atoms/Checkbox/Checkbox';
import { Dialog } from '@/components/atoms/Dialog/Dialog';
import { Label } from '@/components/atoms/Label/Label';
import { Option } from '@/components/atoms/Select/Select';

import { ProfileSummary } from '@/components/molecules/ProfileSummary/ProfileSummary';

import { CareCircleInvite } from '@/components/organisms/CareCircleInvite/CareCircleInvite';

import { CardContainer } from '@/components/templates/CardContainer/CardContainer';
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';

// models
import { CareCircle, CareCircleMemberInvite, Message } from '@karehero/models';

/**
 * CareCircleMemberSelector allows the user to select multiple values from a list of
 * careCircle in the form of checkboxes.
 */
export const CareCircleMemberSelector = ({
  id,
  className,
  careCircle,
  maxColumns,
  isDisabled = false,
  relationshipOptions,
  onInvite,
  label,
  description,
  tooltip,
  isRequired = false,
  isHideTitle = false,
  onChange,
  onMemberSelect,
  value,
  isPrimaryCaregiver = false,
  isCareRecipient = false,
}: CareCircleMemberSelectorProps) => {
  // state
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [valueArray, setValueArray] = useState<string[]>([]);

  // effects
  useEffect(() => {
    try {
      setValueArray(JSON.parse(value || '[]'));
    } catch (e) {
      console.error('Failed to parse value:', value, e);
      setValueArray([]);
    }
  }, [value]);

  // methods
  const handleCheckboxChange = useCallback(
    (isChecked: boolean, optionValue: string) => {
      setValueArray((prevValueArray) => {
        const updatedValueArray = isChecked
          ? [...prevValueArray, optionValue]
          : prevValueArray.filter((v: string) => v !== optionValue);

        onChange?.(JSON.stringify(updatedValueArray));
        onMemberSelect?.(updatedValueArray);
        return updatedValueArray;
      });
    },
    [onChange],
  );

  const handleInvite = useCallback(
    async (careCircleMemberInvite: CareCircleMemberInvite) => {
      try {
        const res = await onInvite?.(careCircleMemberInvite);
        if (!res || 'error' in res) return;
        setIsInviteOpen(false);
      } catch (e) {
        console.error(e);
      }
    },
    [onInvite],
  );

  // memos
  const filteredCareCircleMembers = useMemo(
    () =>
      careCircle.careCircleMembers
        .filter((c) => {
          if (!isPrimaryCaregiver) {
            return c.roles[0].name !== 'Primary Caregiver';
          }
          return true;
        })
        .filter((c) => {
          if (!isCareRecipient) return c.roles[0].name !== 'Care Recipient';
          return true;
        }),
    [isPrimaryCaregiver, isCareRecipient],
  );

  return (
    <BaseField
      id={id}
      className={className}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      {filteredCareCircleMembers.length > 0 && (
        <Wrapper>
          <CardContainer maxColumns={maxColumns ?? 1} size='sm'>
            {filteredCareCircleMembers.map((member: any) => (
              <MemberRow
                key={member.account.id}
                onClick={() => {
                  handleCheckboxChange(
                    !valueArray.includes(member.account.email),
                    member.account.email,
                  );
                }}
              >
                <MemberInfo>
                  <ProfileSummary
                    fallback={member?.account?.firstName?.charAt(0)}
                    size='sm'
                    role={member.roles[0].name}
                    name={`${member.account.firstName || ''} ${
                      member.account.lastName || ''
                    }`}
                  />
                </MemberInfo>
                <Checkbox
                  id={`${id}-${member.account.id}`}
                  size={'sm'}
                  value={!!valueArray.includes(member.account.email)}
                  onChange={(val) =>
                    handleCheckboxChange(val, member.account.email)
                  }
                  isDisabled={isDisabled}
                />
              </MemberRow>
            ))}
          </CardContainer>
        </Wrapper>
      )}
      <InviteButtonWrapper>
        <Button
          ariaLabel='invite new member'
          variant='secondary'
          iconLeft={'plus'}
          onPress={() => setIsInviteOpen(true)}
          isDisabled={isDisabled}
        >
          Invite new member
        </Button>
      </InviteButtonWrapper>
      <Dialog isOpen={isInviteOpen} setIsOpen={setIsInviteOpen}>
        <CareCircleInvite
          careCircle={careCircle}
          onInvite={handleInvite}
          onCancel={() => setIsInviteOpen(false)}
          relationshipOptions={relationshipOptions}
        />
      </Dialog>
    </BaseField>
  );
};

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e0e0e0;
  padding: ${(props) => props.theme.spacing[8]};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
`;

const RoleLabel = styled(Label)`
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.body['sm']};
`;

const MemberDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${(props) => props.theme.spacing[8]};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: ${(props) => props.theme.spacing[8]};
  margin-bottom: ${(props) => props.theme.spacing[8]};
  max-height: 200px;
  overflow: auto;
`;

const InviteButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
`;

export interface CareCircleMemberSelectorProps extends FieldProps<string> {
  className?: string;
  careCircle: CareCircle;
  relationshipOptions: Option[];
  onInvite?: (
    careCircleMemberInvite: CareCircleMemberInvite,
  ) => Promise<Message | undefined>;
  maxColumns?: number;
  onMemberSelect?: (value: string[]) => void;
  isPrimaryCaregiver?: boolean;
  isCareRecipient?: boolean;
}

CareCircleMemberSelector.displayName = 'CareCircleMemberSelector';

export default CareCircleMemberSelector;
